import React from 'react';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'services/localization';
import { AccountingRow, ReceiptAccountingRow } from 'common/types';
import { getPricingString } from 'common/utils/common';
import AccountingToolbar from './AccountingToolbar';
import ReceiptTable from './ReceiptTable';
import { createStyles, makeStyles } from '@mui/styles';
import { Theme } from '@mui/material';

interface AccountingTableRow {
	id: string;
	name: string;
	units: number | '';
	excl_tax: string | '';
	tax: string | '';
	tax_percent: string | '';
	total: string;
	payment_ids: string;
}

interface AccountingDataGridColDef extends GridColDef {
	field: keyof AccountingTableRow;
}

interface Props {
	rows: AccountingRow[];
	tableName?: string;
	receiptRows?: (row: AccountingTableRow) => ReceiptAccountingRow[];
	startReceiptPrint?: (receiptId: string) => void;
	exportHeaders: string[];
	exportFileName: string;
}

const AccountingDataGrid = (props: Props) => {
	const apiRef = useGridApiRef();
	useStyles();
	const { t } = useTranslation();

	const columns: AccountingDataGridColDef[] = [
		{
			field: 'name',
			headerName: t('common:form.name'),
			flex: 2,
			minWidth: 150,
		},
		{
			field: 'units',
			headerName: t('common:count.units', 'Units'),
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'excl_tax',
			headerName: t('common:taxation.exclTax'),
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'tax',
			headerName: t('common:taxation.tax'),
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'tax_percent',
			headerName: `${t('common:taxation.tax')} %`,
			flex: 1,
			minWidth: 100,
		},
		{
			field: 'total',
			headerName: t('common:count.total', 'Total'),
			flex: 1,
			minWidth: 100,
		},
	];

	const accountingRowsToTableRow = (data: AccountingRow[]): AccountingTableRow[] => {
		return data.map((row) => ({
			id: row.id,
			name: row.name,
			units: row.units ?? '',
			excl_tax: row.netAmount ? getPricingString(row.netAmount) : '',
			tax: row.taxAmount ? getPricingString(row.taxAmount) : '',
			tax_percent: row.taxPercent?.toString() ?? '',
			total: row.totalAmount ? getPricingString(row.totalAmount) : '',
			payment_ids: row.paymentIds.join(', '),
		}));
	};

	return (
		<RentleDataGrid
			checkboxSelection={false}
			apiRef={apiRef}
			columns={columns}
			rows={accountingRowsToTableRow(props.rows)}
			autoHeight
			density="compact"
			pagination={false}
			disableColumnMenu
			disableColumnSelector
			components={{
				Toolbar: AccountingToolbar,
			}}
			componentsProps={{
				toolbar: {
					tableName: props.tableName,
					exportHeaders: props.exportHeaders,
					exportFileName: props.exportFileName,
				},
			}}
			hideFooter
			getDetailPanelContent={({ row }) => {
				const receiptRows = props.receiptRows?.(row) ?? [];
				if (!receiptRows?.length) return undefined;
				return (
					<ReceiptTable
						rows={receiptRows}
						startReceiptPrint={props.startReceiptPrint ?? (() => undefined)}
					/>
				);
			}}
			getDetailPanelHeight={() => 'auto'}
			defaultSortModel={[
				{
					field: 'name',
					sort: 'asc',
				},
			]}
		/>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		'@global': {
			/**
			 * For print: Override MUI Data grid styles that rely on calculating absolute width values for cells
			 * This breaks site printing because the values are not responsive
			 * These stylings make the data grid table cells responsive for printing
			 * */
			'@media print': {
				'.MuiDataGrid-columnHeadersInner': {
					width: '100% !important',
					'& > div': {
						width: '100% !important',
					},
				},
				'.MuiDataGrid-virtualScrollerRenderZone': {
					width: '100% !important',
				},
				'.MuiDataGrid-row': {
					width: '100% !important',
					pageBreakInside: 'avoid',
					pageBreakAfter: 'auto',
				},
				'.MuiDataGrid-columnHeaders + div': {
					width: '100% !important',
				},
				'.MuiDataGrid-virtualScroller': {
					width: '100% !important',
				},
				'.MuiDataGrid-columnHeader:first-child': {
					display: 'none',
				},
				'.MuiDataGrid-cell:first-child': {
					display: 'none',
				},
				'.MuiDataGrid-cell:empty': {
					display: 'none',
				},
				'.MuiDataGrid-columnHeader:nth-child(2)': {
					flex: 2,
					maxWidth: 'none !important',
					minWidth: 'unset !important',
				},
				'.MuiDataGrid-cell:nth-child(2)': {
					flex: 2,
					maxWidth: 'none !important',
					minWidth: 'unset !important',
				},
				'.MuiDataGrid-columnHeader': {
					flex: 1,
					maxWidth: 'none !important',
					minWidth: 'unset !important',
					pageBreakInside: 'avoid',
					pageBreakAfter: 'auto',
				},
				'.MuiDataGrid-cell': {
					flex: 1,
					maxWidth: 'none !important',
					minWidth: 'unset !important',
					pageBreakInside: 'avoid',
					pageBreakAfter: 'auto',
				},
			},
		},
	}),
);

export default AccountingDataGrid;
